import { useState, useEffect } from 'react';

interface OEmbedData {
  title?: string;
  author_name?: string;
  author_url?: string;
  provider_name?: string;
  thumbnail_url?: string;
  html?: string;
  [key: string]: unknown;
}

type SupportedPlatform = 'YouTube' | 'YouTube Shorts' | 'TikTok' | 'Instagram' | 'unknown';

function detectPlatform(url: string): SupportedPlatform {
  if (/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//.test(url)) {
    if (/\/shorts\//.test(url)) return 'YouTube Shorts';
    return 'YouTube';
  }
  if (/^(https?:\/\/)?(www\.)?tiktok\.com\//.test(url)) {
    return 'TikTok';
  }
  if (/^(https?:\/\/)?(www\.)?instagram\.com\/(reel|reels|p)\//.test(url)) {
    return 'Instagram';
  }
  return 'unknown';
}

function extractVideoId(url: string, platform: SupportedPlatform): string | undefined {
  if (platform === 'YouTube' || platform === 'YouTube Shorts') {
    const regExp =
      /^https?:\/\/(?:www\.youtube(?:-nocookie)?\.com\/|m\.youtube\.com\/|youtube\.com\/)?(?:ytscreeningroom\?vi?=|youtu\.be\/|vi?\/|user\/.+\/u\/\w{1,2}\/|embed\/|shorts\/|watch\?(?:.*&)?vi?=|&vi?=|\?(?:.*&)?vi?=)([^#&?\n/<>"']*)/i;
    const match = url.match(regExp);
    return match && match[1].length === 11 ? match[1] : undefined;
  }
  if (platform === 'TikTok') {
    // TikTok video ID is usually the last segment of the path
    try {
      const u = new URL(url);
      const segments = u.pathname.split('/');
      return segments.pop() || segments.pop();
    } catch {
      return undefined;
    }
  }
  if (platform === 'Instagram') {
    try {
      const u = new URL(url);
      const segments = u.pathname.split('/').filter(Boolean);
      // URL pattern: /reel/{id}/ or /reels/{id}/ or /p/{id}/
      const reelIndex = segments.findIndex((s) => s === 'reel' || s === 'reels' || s === 'p');
      return reelIndex >= 0 ? segments[reelIndex + 1] : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

async function getThumbnail(
  videoId: string | undefined,
  oEmbedData: OEmbedData | undefined,
  platform: SupportedPlatform
): Promise<string | undefined> {
  if (!videoId) return undefined;
  if (platform === 'YouTube' || platform === 'YouTube Shorts') {
    const defaultHdThumbnail = oEmbedData?.thumbnail_url;
    if (defaultHdThumbnail) {
      const maxResThumbnail = defaultHdThumbnail.replace(/hqdefault\.jpg$/, 'maxresdefault.jpg');
      const maxResIsValid = await fetch(maxResThumbnail, { method: 'HEAD' })
        .then((res) => res.ok)
        .catch(() => false);
      if (maxResIsValid) {
        return maxResThumbnail;
      }
    }
    return defaultHdThumbnail;
  }
  if (platform === 'TikTok') {
    // TikTok oEmbed provides a usable thumbnail_url as-is, so we can use that from oEmbed data
    return undefined;
  }
  return undefined;
}

export default function useEmbeddedVideo(videoUrl: string) {
  const [oEmbedData, setOEmbedData] = useState<OEmbedData | undefined>(undefined);
  const [thumbnail, setThumbnail] = useState<string | undefined>(undefined);
  useEffect(() => {
    async function fetchOembed() {
      if (!videoUrl) {
        setOEmbedData(undefined);
        return;
      }
      try {
        const response = await fetch('/api/video/video-oembed', {
          body: JSON.stringify({ videoUrl }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });
        if (response.ok) {
          const json = await response.json();
          setOEmbedData(json?.data);
        } else {
          setOEmbedData(undefined);
        }
      } catch {
        setOEmbedData(undefined);
      }
    }
    fetchOembed();
  }, [videoUrl]);

  useEffect(() => {
    async function fetchThumbnail() {
      if (!videoUrl) {
        setThumbnail(undefined);
        return;
      }
      const extractedVideoType = detectPlatform(videoUrl);
      const extractedVideoId = extractVideoId(videoUrl, extractedVideoType);
      const thumbnailUrl = await getThumbnail(extractedVideoId, oEmbedData, extractedVideoType);
      setThumbnail(thumbnailUrl);
    }
    fetchThumbnail();
  }, [videoUrl, oEmbedData]);

  if (!videoUrl) {
    return {
      extractedVideoType: 'unknown',
      extractedVideoId: undefined,
      extractedVideoThumbnail: undefined,
      extractedIsPortrait: false,
      oEmbedData: undefined,
    };
  }
  const extractedVideoType = detectPlatform(videoUrl);
  const extractedVideoId = extractVideoId(videoUrl, extractedVideoType);
  const extractedIsPortrait =
    extractedVideoType === 'YouTube Shorts' ||
    extractedVideoType === 'TikTok' ||
    extractedVideoType === 'Instagram';

  let finalThumbnail;
  // Prefer oEmbed thumbnail for TikTok and Instagram
  if (
    (extractedVideoType === 'TikTok' || extractedVideoType === 'Instagram') &&
    oEmbedData?.thumbnail_url
  ) {
    finalThumbnail = oEmbedData.thumbnail_url;
  } else {
    finalThumbnail = thumbnail;
  }

  console.log('useEmbeddedVideo thumbnail debug:', {
    videoUrl,
    extractedVideoType,
    extractedVideoId,
    oEmbedThumbnail: oEmbedData?.thumbnail_url,
    hookThumbnail: thumbnail,
    resolvedThumbnail: finalThumbnail,
  });

  return {
    extractedVideoType,
    extractedVideoId,
    extractedVideoThumbnail: finalThumbnail,
    extractedIsPortrait,
    oEmbedData,
  };
}
